import { useState } from 'react';

import {
  Button,
  Card,
  CardTitle,
  Flex,
  Tag,
  Text,
} from '@spotify-internal/encore-web';
import {
  Copy,
  FormTextField,
  useAlert,
  useAuth,
  useAuthFactory,
} from '@spotify/confidence-core-react';

import styles from './AuthDebug.module.css';

export const AuthDebug = () => {
  const alert = useAlert();
  const auth = useAuth();
  const { overrideToken, setOverrideToken } = useAuthFactory();
  const [authToken, setAuthToken] = useState(overrideToken ?? '');

  const getToken = async (): Promise<string | undefined> => {
    const token =
      overrideToken || auth.token || (await auth.getAccessTokenSilently());
    if (!token) {
      alert.post({
        message: 'Auth token unavailable',
        severity: 'error',
        display: 'transient',
      });
    }
    return token || undefined;
  };

  return (
    <Flex direction="column" gap="base">
      <Flex alignItems="center" gap="tighter-2">
        <Text variant="bodyMediumBold">Authorization</Text>
        {!!overrideToken && (
          <Tag colorSet="warning" role="status">
            Override enabled
          </Tag>
        )}
      </Flex>
      <Card
        id="auth-debug-token"
        variant="bordered"
        className={styles.card}
        title={<CardTitle variant="bodyMediumBold">Token</CardTitle>}
        trailing={
          <Copy
            value={getToken}
            label="Copy"
            className={styles.copy}
            successMessage="Auth token copied to clipboard"
          />
        }
        body={
          <FormTextField
            name="auth-token"
            label="Custom auth token"
            placeholder="Enter a custom auth token"
            value={authToken}
            onChange={e => setAuthToken(e.currentTarget.value)}
            autoComplete="off"
            helperText="Override the auth token for the current session. This will be used for all requests to Confidence services until the override is cleared."
           data-track-source-anchor="5424eb8ae2"/>
        }
        footer={
          <Flex justifyContent="end" gap="tighter-2">
            <Button
              variant="tertiary"
              disabled={!overrideToken}
              onClick={() => setOverrideToken(undefined)}
             data-track-source-anchor="4decb31b11">
              Clear override
            </Button>
            <Button
              variant="secondary-bordered"
              disabled={!authToken}
              onClick={() => setOverrideToken(authToken)}
             data-track-source-anchor="3468deee42">
              Override
            </Button>
          </Flex>
        }
      />
    </Flex>
  );
};
